import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Chip, Grid, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';

import {
  DashboardCard,
  TrainingLoadStatusChip,
  VolumeIntensityDetail,
  TrainingLoadDetail,
  ExercisesAndPRsDetail,
  SessionQualityDetail,
  RecoveryDetail,
} from './session-report-components';

const WorkoutSessionReport = ({ report }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedSection, setSelectedSection] = useState(null);

  const {
    title,
    session_name,
    session_date,
    generated_at,
    time_metrics,
    volume_metrics,
    intensity_metrics,
    cardio_metrics,
    biomechanics,
    exercise_analyses,
    pr_summary,
    consistency_metrics,
    recent_training_context,
    achievements,
    future_context,
    ai_analysis,
    duration_minutes,
    executive_summary,
    coach_insights,
    activity_reports,
  } = report;

  const duration = time_metrics?.elapsed_duration_seconds 
    ? Math.round(time_metrics.elapsed_duration_seconds / 60) 
    : duration_minutes;

  const totalVolume = volume_metrics?.total_volume_load ?? biomechanics?.total_volume_load ?? 0;
  const totalSets = volume_metrics?.total_sets ?? 0;
  const totalDistance = cardio_metrics?.total_distance_meters || 0;
  const totalCalories = cardio_metrics?.estimated_calories_burned || 0;
  const avgHeartRate = cardio_metrics?.average_heart_rate || 0;

  // Determine Primary Metric for Card 1
  let primaryMetric = { label: 'Total Volume', value: totalVolume, unit: volume_metrics?.volume_unit || 'lbs', icon: FitnessCenterIcon, color: 'primary' };
  let secondaryMetric = null;

  if (totalVolume > 0) {
    if (totalDistance > 0 || totalCalories > 0) {
      const parts = [];
      if (totalDistance > 0) parts.push(`${totalDistance > 1000 ? (totalDistance/1000).toFixed(1) + 'km' : totalDistance + 'm'}`);
      if (totalCalories > 0) parts.push(`${totalCalories} kcal`);
      secondaryMetric = parts.join(' · ');
    } else if (totalSets > 0) {
      secondaryMetric = `${totalSets} sets`;
    }
  } else if (totalDistance > 0) {
    primaryMetric = { 
      label: 'Total Distance', 
      value: totalDistance > 1000 ? (totalDistance / 1000).toFixed(2) : totalDistance, 
      unit: totalDistance > 1000 ? 'km' : 'm', 
      icon: DirectionsRunIcon, 
      color: 'error' 
    };
    if (totalCalories > 0) secondaryMetric = `${totalCalories} kcal`;
    else if (avgHeartRate > 0) secondaryMetric = `${avgHeartRate} bpm avg`;
  } else if (totalCalories > 0) {
    primaryMetric = { label: 'Calories Burned', value: totalCalories, unit: 'kcal', icon: LocalFireDepartmentIcon, color: 'warning' };
    if (avgHeartRate > 0) secondaryMetric = `${avgHeartRate} bpm avg`;
  } else {
    primaryMetric = { label: 'Duration', value: duration, unit: 'min', icon: AccessTimeIcon, color: 'info' };
  }

  const { totalPRs, allTimePRs } = useMemo(() => {
    const prMap = new Map();
    if (pr_summary?.notable_prs?.length > 0) {
      pr_summary.notable_prs.forEach(pr => {
        const key = `${pr.exercise_name}-${pr.pr_type || 'default'}`;
        if (!prMap.has(key)) {
          prMap.set(key, pr);
        }
      });
    }
    if (prMap.size === 0) {
      const rawExercises = exercise_analyses?.length > 0 ? exercise_analyses : activity_reports;
      rawExercises?.filter(ex => ex.is_all_time_pr || ex.is_session_pr || ex.is_personal_record)?.forEach(ex => {
        const exerciseName = ex.activity_name || ex.exercise_name || 'Unknown';
        const key = `${exerciseName}-${ex.pr_types?.[0] || 'default'}`;
        if (!prMap.has(key)) {
          prMap.set(key, { is_all_time: ex.is_all_time_pr });
        }
      });
    }
    const prs = Array.from(prMap.values());
    return {
      totalPRs: prs.length,
      allTimePRs: prs.filter(p => p.is_all_time).length,
    };
  }, [pr_summary, exercise_analyses, activity_reports]);

  const exerciseCount = useMemo(() => {
    const raw = exercise_analyses?.length > 0 ? exercise_analyses : activity_reports;
    if (!raw) return 0;
    const unique = new Set(raw.map(e => e.exercise_definition_id || e.activity_name));
    return unique.size;
  }, [exercise_analyses, activity_reports]);

  const qualityScore = ai_analysis?.session_quality_score;
  const trainingStatus = recent_training_context?.overall_status;
  const recoveryHours = future_context?.recovery?.total_recommended_rest_hours || future_context?.recommended_recovery_hours;
  const currentStreak = consistency_metrics?.current_streak_days;

  const displayDate = session_date || generated_at;
  const formattedDate = displayDate 
    ? new Date(displayDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const handleBackToOverview = useCallback(() => setSelectedSection(null), []);

  // Detail view routing
  if (selectedSection) {
    switch (selectedSection) {
      case 'volume':
        return <VolumeIntensityDetail volumeMetrics={volume_metrics} intensityMetrics={intensity_metrics} cardioMetrics={cardio_metrics} biomechanics={biomechanics} onBack={handleBackToOverview} />;
      case 'training':
        return <TrainingLoadDetail recentContext={recent_training_context} consistency={consistency_metrics} onBack={handleBackToOverview} />;
      case 'exercises':
        return <ExercisesAndPRsDetail prSummary={pr_summary} exerciseAnalyses={exercise_analyses} activityReports={activity_reports} onBack={handleBackToOverview} />;
      case 'quality':
        return <SessionQualityDetail aiAnalysis={ai_analysis} legacyInsights={coach_insights} timeMetrics={time_metrics} legacyDuration={duration_minutes} onBack={handleBackToOverview} />;
      case 'recovery':
        return <RecoveryDetail futureContext={future_context} recentContext={recent_training_context} onBack={handleBackToOverview} />;
      default:
        setSelectedSection(null);
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
            {title?.replace('Report: ', '') || session_name || 'Workout Report'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {trainingStatus && trainingStatus !== 'optimal' && (
              <TrainingLoadStatusChip status={trainingStatus} />
            )}
            {totalPRs > 0 && (
              <Chip icon={<EmojiEventsIcon />} label={`${totalPRs} PR${totalPRs > 1 ? 's' : ''}`} color="warning" size="small" sx={{ fontWeight: 600 }} />
            )}
          </Box>
        </Box>
        {formattedDate && (
          <Typography variant="body2" color="text.secondary">{formattedDate}</Typography>
        )}
      </Box>

      {/* AI Summary */}
      {(ai_analysis?.executive_summary || executive_summary) && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            mb: 3, 
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRadius: 1,
          }}
        >
          {ai_analysis?.headline && (
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>{ai_analysis.headline}</Typography>
          )}
          <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
            {ai_analysis?.executive_summary || executive_summary}
          </Typography>
        </Paper>
      )}

      {/* Dashboard Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        
        {/* Card 1: Session Quality */}
        <Grid item xs={6} sm={4} md={3}>
          <DashboardCard
            title="Session Quality"
            value={qualityScore > 0 ? `${qualityScore.toFixed(0)}/100` : duration || '—'}
            unit={qualityScore > 0 ? undefined : 'min'}
            icon={qualityScore > 0 ? SpeedIcon : AccessTimeIcon}
            color={qualityScore >= 80 ? 'success' : qualityScore >= 60 ? 'primary' : qualityScore > 0 ? 'warning' : 'info'}
            onClick={() => setSelectedSection('quality')}
            progress={qualityScore > 0 ? qualityScore : undefined}
            secondaryValue={qualityScore > 0 && duration ? `${duration} min` : time_metrics?.efficiency_percent ? `${time_metrics.efficiency_percent.toFixed(0)}% efficient` : undefined}
            status={qualityScore > 0 ? { label: qualityScore >= 80 ? 'Excellent' : qualityScore >= 60 ? 'Good' : 'Fair', color: qualityScore >= 80 ? 'success' : qualityScore >= 60 ? 'primary' : 'warning' } : undefined}
          />
        </Grid>

        {/* Card 2: Training Load */}
        {(recent_training_context || consistency_metrics) && (
          <Grid item xs={6} sm={4} md={3}>
            <DashboardCard
              title="Training Load"
              value={recent_training_context?.sessions_last_7_days ?? consistency_metrics?.sessions_this_week ?? 0}
              unit="sessions/wk"
              icon={PsychologyIcon}
              color={trainingStatus === 'optimal' ? 'success' : trainingStatus?.includes('over') ? 'error' : currentStreak > 0 ? 'warning' : 'primary'}
              onClick={() => setSelectedSection('training')}
              status={trainingStatus ? { label: trainingStatus?.replace(/_/g, ' '), color: trainingStatus === 'optimal' ? 'success' : 'warning', severity: trainingStatus } : undefined}
              secondaryValue={currentStreak > 0 ? `🔥 ${currentStreak} day streak` : recent_training_context?.accumulated_fatigue_score ? `${recent_training_context.accumulated_fatigue_score.toFixed(0)}% fatigue` : undefined}
            />
          </Grid>
        )}

        {/* Card 3: Work Output (Dynamic) */}
        <Grid item xs={6} sm={4} md={3}>
           <DashboardCard
             title={primaryMetric.label}
             value={primaryMetric.value?.toLocaleString()}
             unit={primaryMetric.unit}
             icon={primaryMetric.icon}
             color={primaryMetric.color}
             onClick={() => setSelectedSection('volume')}
             secondaryValue={secondaryMetric}
             trend={volume_metrics?.vs_last_session_percent > 0 ? 'improving' : volume_metrics?.vs_last_session_percent < 0 ? 'declining' : undefined}
             disabled={!primaryMetric.value}
           />
        </Grid>

        {/* Card 4: PRs / Exercises */}
        <Grid item xs={6} sm={4} md={3}>
          <DashboardCard
            title={totalPRs > 0 ? "Personal Records" : "Exercises"}
            value={totalPRs > 0 ? totalPRs : exerciseCount}
            icon={totalPRs > 0 ? EmojiEventsIcon : FitnessCenterIcon}
            color={totalPRs > 0 ? 'warning' : 'success'}
            onClick={() => setSelectedSection('exercises')}
            secondaryValue={totalPRs > 0 ? `${exerciseCount} exercises` : undefined}
            status={allTimePRs > 0 ? { label: 'All-Time!', color: 'warning', severity: 'success' } : undefined}
            disabled={exerciseCount === 0}
          />
        </Grid>

        {/* Card 5: Recovery */}
        {(future_context?.recovery || recoveryHours) && (
          <Grid item xs={6} sm={4} md={3}>
            <DashboardCard
              title="Recovery"
              value={recoveryHours || future_context?.recovery?.overall_recovery_hours || 24}
              unit="hours"
              icon={HealthAndSafetyIcon}
              color="info"
              onClick={() => setSelectedSection('recovery')}
              status={future_context?.recovery?.suggested_rest_day ? { label: 'Rest Suggested', color: 'warning' } : undefined}
            />
          </Grid>
        )}
      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
          Tap any card above for detailed analysis
        </Typography>
      </Box>
    </Box>
  );
};

WorkoutSessionReport.propTypes = {
  report: PropTypes.object.isRequired,
};

export default WorkoutSessionReport;